import { useEffect, useState } from "react";
import { TrendUp, ArrowRight } from "@phosphor-icons/react";
import { useWeightUnit } from "@/lib/units";

/**
 * Shows the user's most recent performance for an exercise and a
 * progression suggestion ("Try X kg × Y today"). Tapping the suggestion
 * pre-fills the first set's weight/reps via the onApply callback.
 */
export default function ProgressionCoach({ api, exerciseName, onApply }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { label: weightLabel, formatWeight } = useWeightUnit();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const url = `${api}/exercises/last-performance?name=${encodeURIComponent(exerciseName)}`;
        const res = await fetch(url);
        if (!res.ok) {
          if (!cancelled) setData({ found: false });
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setData({ found: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, exerciseName]);

  if (loading || !data?.found) return null;

  const lastSets = data.sets || [];
  const topSet = lastSets.reduce(
    (best, s) => (((s.weight || 0) > (best.weight || 0)) || ((s.weight || 0) === (best.weight || 0) && (s.reps || 0) > (best.reps || 0)) ? s : best),
    { weight: 0, reps: 0 }
  );

  const sug = data.suggestion || {};
  const sugWeightKg = Number(sug.suggested_weight) || 0;
  const sugReps = Number(sug.suggested_reps) || 0;
  const isDeload = !!sug.is_deload;
  const displaySugWeight = sugWeightKg > 0 ? formatWeight(sugWeightKg, { decimals: 1, withUnit: false }) : "BW";
  const displayLastWeight = (topSet.weight || 0) > 0 ? formatWeight(topSet.weight, { decimals: 1, withUnit: false }) : "BW";

  const ago = (() => {
    if (!data.date) return "";
    const days = Math.floor((Date.now() - new Date(data.date).getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  })();

  const handleApply = () => {
    if (typeof onApply !== "function") return;
    onApply({ weightKg: sugWeightKg, reps: sugReps });
  };

  return (
    <div
      className={`border p-2.5 flex items-center justify-between gap-3 ${
        isDeload
          ? "border-[#8B5CF6]/40 bg-gradient-to-r from-[#8B5CF6]/15 via-transparent to-transparent"
          : "border-[#E05A1D]/30 bg-gradient-to-r from-[#E05A1D]/10 via-transparent to-transparent"
      }`}
      data-testid={`progression-coach-${exerciseName.replace(/\s+/g, "-").toLowerCase()}`}
      title={sug.reason || ""}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <TrendUp size={14} className={`shrink-0 ${isDeload ? "text-[#8B5CF6]" : "text-[#E05A1D]"}`} weight="bold" />
        <div className="min-w-0 flex flex-col leading-tight">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#71717A]">
            {isDeload ? `Deload week · 2 tough sessions in a row` : `Last time · ${ago}`}
          </span>
          <span className="text-xs text-white truncate">
            {displayLastWeight}{(topSet.weight || 0) > 0 ? ` ${weightLabel}` : ""} × {topSet.reps || 0}
          </span>
        </div>
      </div>
      {sug.suggested_reps > 0 && (
        <button
          onClick={handleApply}
          className={`text-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors shrink-0 ${
            isDeload
              ? "bg-[#8B5CF6] hover:bg-[#A78BFA]"
              : "bg-[#E05A1D] hover:bg-[#F06B2E]"
          }`}
          title={sug.reason || "Apply suggestion"}
          data-testid={`progression-apply-${exerciseName.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {isDeload ? "Deload" : "Try"} {displaySugWeight}{sugWeightKg > 0 ? ` ${weightLabel}` : ""} × {sugReps}
          {sug.delta && <span className={isDeload ? "text-[#E9D5FF]" : "text-[#FED7AA]"}>· {sug.delta}</span>}
          <ArrowRight size={11} weight="bold" />
        </button>
      )}
    </div>
  );
}
